import React, { useEffect, useState } from 'react';
import Imgix from 'react-imgix';
import styled, { useTheme } from 'styled-components';
import { BodyFour } from '@/styles/theme/default/typography';
import { SelectedOption, SelectionTitle } from '../../../Styles';
import FabricItem from './FabricItem';
import { ShippingFilterState } from '@/components/customers/product/productBody/sidebar/QuickShipSwitch';
import { getFilteredFabrics } from '@/common/QuickShip';
import handleProductVar
  from "@/components/customers/product/productBody/sidebar/handleProductVar";

const Wrapper = styled.div`
  margin-top: 5px;
  padding: 0 20px;

  .settings-button {
    cursor: pointer;
    padding: 0 0 15px 15px;
  }

  .outdoor-copy {
    margin-top: 24px;
  }

  .fabric-loading-container {
    display: flex;
    justify-content: center;
    margin: 20px 0;
  }

  .fabric-options-container {
    margin-top: 10px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-gap: 15px;
  }

  .order-swatches {
    margin-top: 12px;

    i {
      margin-left: 2px;
    }

    a {
      display: flex;
      flex-direction: column;
      color: ${({ theme }) => theme.colors.french};

      &:hover {
        text-decoration: none;
      }

      img {
        width: 142px;
      }
    }
  }

  .select-title-options {
    display: flex;
    justify-content: space-between;
  }

  @media (max-width: 992px) {
    .fabric-options-container {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
    }
  }

  @media (max-width: 600px) {
    .fabric-options-container {
      grid-template-columns: 1fr 1fr 1fr 1fr;
    }
  }

  @media (max-width: 360px) {
    .fabric-options-container {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }
`;

function Fabric({
  handleShowFabric,
  setHoveredFabric,
  productData,
  featuredFabrics,
  setProductVar,
  fabricId,
  dataFabrics,
  shippingFilter
}) {
  const [selectedFabric, setSelectedFabric] = useState({
    name: null,
    id: null
  });
  const theme = useTheme();

  useEffect(() => {
    setSelectedFabric(productData.fabric);
  }, [productData.fabric.id]);

  const checkSelected = fabric => {
    if (selectedFabric) {
      return parseInt(selectedFabric.id) === parseInt(fabric.id);
    }
    return parseInt(fabricId) === parseInt(fabric.id);
  };

  const isOutdoorCategory = productData?.frame?.subcategories.some(
    x => x?.category?.id == 11
  );

  const attributeName = productData?.fabric?.treatment?.name;
  const isFabricTreatment = productData?.fabric?.treatment.id == 1;

  const isFabricColor = productData?.fabric?.treatment.id == 2;

  const isWallpaper = productData?.frame?.type?.id == 4;

  const filteredFabrics = getFilteredFabrics(
    shippingFilter,
    featuredFabrics,
    productData
  );

  return (
    <Wrapper isFabricColor={isFabricColor}>
      <div className="select-title-options">
        <SelectionTitle>
          Selected {attributeName}
          {isOutdoorCategory ? '*' : ''}
        </SelectionTitle>
      </div>

      <SelectedOption>
        {selectedFabric.id ? (
          <>
            {selectedFabric.name}{' '}
            {selectedFabric.collaborator &&
              `by ${selectedFabric.collaborator.name}`}
          </>
        ) : (
          <>
            {productData.fabric.name}{' '}
            {productData.fabric.collaborator &&
              `by ${productData.fabric.collaborator.name}`}
          </>
        )}
      </SelectedOption>
      <div className="fabric-options-container">
        {sliceFabrics(filteredFabrics).map(fabric => {
          return (
            <FabricItem
              selected={checkSelected(fabric)}
              onClick={() => {
                setSelectedFabric(fabric);
                handleProductVar(setProductVar, fabric.id, productData);
              }}
              key={`${fabric.id}-${fabric.is_available_on_pdp}`}
              name={fabric.name}
              fabric={fabric}
              image={fabric.image}
              setHoveredFabric={setHoveredFabric}
              isFabricColor={isFabricColor}
            />
          );
        })}
        {!isFabricColor && (
          <SeeMoreFabricsButton
            onClick={() => handleShowFabric(productData.fabric)}
          >
            <div className="image-wrapper">
              <BodyFour
                className="see-more-fabrics-txt"
                as="span"
                color={theme.colors.french}
              >
                {getSeeMoreFabricsButtonText(dataFabrics, shippingFilter)}
              </BodyFour>
              <Imgix
                className="see-more-fabric"
                imgixParams={{ auto: 'compress,format' }}
                sizes="
                (min-width: 993px) 90px,
                (min-width: 601px) 16vw,
                (min-width: 361px) 22vw,
                30vw"
                src={`https://theinside.imgix.net/common/see-more-options.jpg`}
                htmlAttributes={{
                  alt: 'product name',
                  loading: 'lazy'
                }}
              />
            </div>
          </SeeMoreFabricsButton>
        )}
      </div>
      {isFabricTreatment && !isWallpaper && (
        <>
          {productData?.frame?.subcategories.some(
            x => x?.category?.id == 11
          ) && (
            <BodyFour className="outdoor-copy" color="#6C6C6C">
              *Please note that outdoor fabric colors may differ from indoor
              fabrics.
            </BodyFour>
          )}
        </>
      )}
    </Wrapper>
  );
}

function sliceFabrics(featuredFabrics) {
  return featuredFabrics.slice(0, 7);
}

function getSeeMoreFabricsButtonText(dataFabrics, shippingFilter) {
  const nonPreferredCount =
    dataFabrics?.productBySlug?.non_preferred_pdp_fabrics?.length;

  if (shippingFilter === ShippingFilterState.QUICK_SHIP || !nonPreferredCount) {
    return 'See More Options';
  }

  return (
    <>
      + {nonPreferredCount}
      <br />
      More
    </>
  );
}

export default Fabric;

const SeeMoreFabricsButton = styled.button`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border: none;
  background: transparent;
  padding: 0 0 100%;

  &:hover {
    outline: none;
  }

  .see-more-fabrics-txt {
    max-width: 90px;
    z-index: 1;
    font-weight: 500;
  }

  .image-wrapper {
    border: 3px solid white;
    position: absolute !important;
    top: 0;
    left: 0;
    background: transparent;
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    .see-more-fabric {
      position: absolute;
      max-width: ${({ imageWidth }) => imageWidth};
      max-height: 100%;
      width: auto;
    }
  }
`;
